import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { analyticsApi } from '@/lib/api';
import { SocialPlatform } from '@/types';
import { queryKeys } from './shared';

// Analytics
export const useAnalyticsOverview = (
  workspaceId: string,
  from?: string,
  to?: string,
  brandId?: string | null,
) =>
  useQuery({
    queryKey: [...queryKeys.analyticsOverview(workspaceId, from, to), brandId],
    queryFn: () => analyticsApi.overview(workspaceId, from, to, brandId ?? undefined),
    enabled: !!workspaceId,
  });

export const usePostAnalytics = (
  workspaceId: string,
  sort?: string,
  dir?: string,
  page?: number,
  size?: number,
) =>
  useQuery({
    queryKey: queryKeys.postAnalytics(workspaceId, sort, dir, page, size),
    queryFn: () => analyticsApi.posts(workspaceId, { sort, dir, page, size }),
    enabled: !!workspaceId,
  });

export const usePostsOverTime = (workspaceId: string, days: number = 30) =>
  useQuery({
    queryKey: ['analytics', workspaceId, 'posts-over-time', days],
    queryFn: () => analyticsApi.postsOverTime(workspaceId, days),
    enabled: !!workspaceId,
    staleTime: 60_000,
  });

export const usePostTimeseries = (workspaceId: string, postId: string | null, days: number = 30) =>
  useQuery({
    queryKey: queryKeys.postTimeseries(workspaceId, postId ?? '', days),
    queryFn: () => analyticsApi.postTimeseries(workspaceId, postId!, days),
    enabled: !!workspaceId && !!postId,
  });

/** V94 — daily aggregate per metric, summed across every post on the given
 *  platform in the workspace. Drives the per-platform tab hero charts. */
export const usePlatformTimeseries = (
  workspaceId: string,
  platform: SocialPlatform | null,
  from?: string,
  to?: string,
  connectionId?: string,
) =>
  useQuery({
    queryKey: ['analytics', workspaceId, 'platform-timeseries', platform, from, to, connectionId],
    queryFn: () => analyticsApi.platformTimeseries(workspaceId, platform!, from, to, connectionId),
    enabled: !!workspaceId && !!platform,
    staleTime: 60_000,
  });

export const useSyncAnalytics = (workspaceId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => analyticsApi.sync(workspaceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['analyticsOverview', workspaceId] });
      qc.invalidateQueries({ queryKey: ['postAnalytics', workspaceId] });
    },
  });
};
